import React, {useState, useEffect, useContext, useRef} from 'react';
import createAuth0Client, {Auth0Client} from '@auth0/auth0-spa-js';
import config from './config';
import {auth} from './setup';
import {getPreviouslyLoggedIn} from './reducers/auth';
import makeComp from './util/profiler';

// this is a monkey patch to get around Auth0's stupid refresh-on-init behavior.
// unfortunately for us, when something goes wrong, it likes to hang / crash.
// so, we override the timeout to a short value and suppress any errors (including timeout).
// `checkSession` erroring out should have no impact except for failing the silent refresh.
// `checkSession` is only called once (on `createAuth0Client` initialization).
const checkSession = Auth0Client.prototype.checkSession;
Auth0Client.prototype.checkSession = async function(opts) {
  console.log('doing patched checkSession');
  try {
    return await checkSession.call(this, {
      ...opts,
      timeoutInSeconds: opts?.timeoutInSeconds ?? 5,
    });
  } catch (e) {
    console.log('Auth0Client.checkSession error. ignoring...', e);
  }
};

// We had to remove `audience` to support custom Auth0 domains, as long as an
// application is flagged as OIDC conformant this is fine:
// https://auth0.com/docs/custom-domains/troubleshoot-custom-domains#-service-not-found-
// > audience: `https://${config.AUTH0_DOMAIN}/userinfo`
const initOptions = {
  domain: config.AUTH0_DOMAIN,
  client_id: config.AUTH0_CLIENT_ID,
  redirect_uri: `${document.location.origin}/login`,
  scope: 'openid profile email',
  leeway: 24 * 3600,
  cacheLocation: 'localstorage' as 'localstorage',
  useRefreshTokens: true,
};

export const Auth0Context = React.createContext({});
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider: React.FC = makeComp(
  ({children}) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);

    let resolveClient: (client: Auth0Client) => void;
    const clientPromiseRef = useRef(
      new Promise<Auth0Client>(res => (resolveClient = res))
    );

    const auth0Client = waitUntilReady(clientPromiseRef.current);
    if (auth != null && 'auth0Client' in auth && auth.auth0Client == null) {
      auth.auth0Client = auth0Client;
    }

    useEffect(() => {
      const initAuth0 = async () => {
        const auth0FromHook = new Auth0Client(initOptions);
        // This useEffect happens once on page load. We call auth0 checkSession to automatically refresh
        // the user's token if we need to, before making our first request (which would cause the user
        // to go through the login flow again because of our middleware).
        // We check if we should even try to do a refresh here, because we don't want to make an auth0
        // request if we're running in Googlebot. Auth0 blocks Googlebot with robots.txt, which causes a
        // 60s delay in which the page doesn't load, which means we don't get indexed.
        try {
          if (getPreviouslyLoggedIn()) {
            await auth0FromHook.checkSession();
          }
        } catch (e) {
          // the Auth0 client fails to load when its cache is busted (like if it has an invalid refresh token).
          // to compensate, we clear the cache and re-init. this doesn't cause the user to get logged out.
          clearAuth0Cache();
          await createAuth0Client(initOptions);
        }
        resolveClient(auth0FromHook);

        setIsAuthenticated(await auth0FromHook.isAuthenticated());

        setLoading(false);
      };
      initAuth0();
      // eslint-disable-next-line
    }, []);

    return (
      <Auth0Context.Provider
        value={{
          isAuthenticated,
          loading,
          auth0Client,
        }}>
        {children}
      </Auth0Context.Provider>
    );
  },
  {id: 'Auth0Provider'}
);

const CLIENT_METHODS = [
  'handleRedirectCallback',
  'getIdTokenClaims',
  'loginWithRedirect',
  'getTokenSilently',
  'getTokenWithPopup',
  'logout',
] as const;
type ClientMethodTuple = typeof CLIENT_METHODS;
type ClientMethod = ClientMethodTuple[number];

export type WaitingAuth0Client = Pick<Auth0Client, ClientMethod>;

function waitUntilReady(clientPromise: Promise<Auth0Client>) {
  const waitingClient = {} as WaitingAuth0Client;
  CLIENT_METHODS.forEach(
    m =>
      (waitingClient[m] = async (...args: any[]) => {
        const client = await clientPromise;
        return client[m](...args);
      })
  );
  return waitingClient;
}

const AUTH0_LOCALSTORAGE_KEY_PREFIX = '@@auth0spajs@@';

function clearAuth0Cache(): void {
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    // TODO(axel): make this less brittle. it shouldn't break if Auth0's key prefix changes.
    if (k != null && k.startsWith(AUTH0_LOCALSTORAGE_KEY_PREFIX)) {
      localStorage.removeItem(k);
      break;
    }
  }
}
